import { useMutation } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import { sanitizeReturnTo } from '@sentinel/auth-core';
import { SKIP_FORBIDDEN_HEADER, SKIP_UNAUTH_HEADER } from '@sentinel/api-client';
import { useApi } from '../../lib/api';

/**
 * Passkey login is unauthenticated and renders its own errors, so both calls
 * skip the global handlers. A 403 here is an IP-policy or risk block, which
 * the login screen already explains.
 */
const PASSKEY_LOGIN_HEADERS = {
  [SKIP_UNAUTH_HEADER]: '1',
  [SKIP_FORBIDDEN_HEADER]: '1',
} as const;

export interface PasskeyLoginInput {
  email: string;
  next?: string;
  authorize_request_id?: string;
}

export interface PasskeyLoginResult {
  redirect: string;
}

/**
 * Drives the passkey-login ceremony:
 *   1. POST /auth/webauthn/authenticate/start  → challenge + options
 *   2. navigator.credentials.get (via @simplewebauthn/browser)
 *   3. POST /auth/webauthn/authenticate/finish → server attaches HttpOnly
 *      session cookies (access, refresh, csrf, family, optional sso-hint)
 *      directly. JSON body still echoes the token pair for back-compat but
 *      the SPA does not consume it.
 *
 * No SPA-side cookie-graduation step required — the finish endpoint sets
 * cookies natively. We just compute the post-login redirect.
 */
export function useWebAuthnLogin() {
  const api = useApi();
  return useMutation({
    mutationFn: async (input: PasskeyLoginInput): Promise<PasskeyLoginResult> => {
      const startRes = await api.POST('/auth/webauthn/authenticate/start', {
        body: { email: input.email },
        headers: PASSKEY_LOGIN_HEADERS,
      });
      if (startRes.error) throw startRes.error;
      const { challenge_id, options } = startRes.data!;

      // The server returns the full request-options shape wrapped in
      // `{ mediation, publicKey: { challenge, allowCredentials, ... } }`
      // (webauthn-rs standard, mirroring use-webauthn-mfa.ts:53-58). simplewebauthn's
      // `startAuthentication` wants the inner `publicKey` block as `optionsJSON`,
      // and the top-level `mediation: conditional` belongs in `useBrowserAutofill`,
      // not inside the publicKey options. Without this unwrap the call hangs
      // because `optionsJSON.challenge` is undefined and the library never
      // proceeds to `navigator.credentials.get()`.
      const wrapped = options as { publicKey?: unknown } | undefined;
      const optionsJSON = (
        wrapped && typeof wrapped === 'object' && 'publicKey' in wrapped
          ? wrapped.publicKey
          : (wrapped as unknown)
      ) as Parameters<typeof startAuthentication>[0]['optionsJSON'];

      const assertion = await startAuthentication({ optionsJSON });

      const finishRes = await api.POST('/auth/webauthn/authenticate/finish', {
        body: { challenge_id, credential: assertion },
        headers: PASSKEY_LOGIN_HEADERS,
      });
      if (finishRes.error) throw finishRes.error;

      const redirect =
        input.authorize_request_id && input.authorize_request_id.length > 0
          ? `/auth/authorize?authorize_request_id=${encodeURIComponent(input.authorize_request_id)}`
          : sanitizeReturnTo(input.next);
      return { redirect };
    },
  });
}
